import { useEffect, useState } from "react";
import { Sparkles, Clock, CheckCircle2, XCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api } from "../../lib/api";

const EXTRA_LABELS = {
  lavandaria: "Lavandaria",
  consumiveis: "Consumíveis",
  limpeza_profunda: "Limpeza profunda",
  limpeza_exterior: "Limpeza exterior",
  relva: "Corte de relva",
  manutencao: "Manutenção",
};

const STATUS_STYLES = {
  pending:  { color: "text-[#f59e0b]", border: "border-[#f59e0b]/40", label: "Pendente", icon: Clock },
  approved: { color: "text-[#10b981]", border: "border-[#10b981]/40", label: "Aprovado", icon: CheckCircle2 },
  rejected: { color: "text-[#fca5a5]", border: "border-[#fca5a5]/40", label: "Rejeitado", icon: XCircle },
};

/**
 * EmployeeExtras — history of suggestions the employee has made. Status is
 * updated by the admin. Used as both a transparency log and a way for the
 * employee to see what was approved / rejected.
 */
export default function EmployeeExtras() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/employee/extra-suggestions")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = items.reduce((acc, it) => {
    (acc[it.status] = acc[it.status] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-3">Sugestões</div>
        <h1 className="font-display text-5xl">Serviços extra sugeridos.</h1>
        <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Extras que sugeriu às suas limpezas. Ficam pendentes até serem aprovados ou rejeitados pelo admin.
        </div>
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="emp-extras-empty">
          <Sparkles className="mx-auto text-[#d4af37] mb-3" size={28} />
          Ainda não sugeriu nenhum extra. Durante uma tarefa em curso pode propor serviços adicionais.
        </div>
      )}

      {["pending", "approved", "rejected"].map(s => {
        const list = grouped[s] || [];
        if (list.length === 0) return null;
        const st = STATUS_STYLES[s];
        const Ic = st.icon;
        return (
          <section key={s} data-testid={`emp-extras-${s}`}>
            <div className={`eyebrow mb-3 flex items-center gap-2 ${st.color}`}>
              <Ic size={13} />
              {st.label} · <span className="text-[#a0b2af] normal-case tracking-normal">{list.length}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {list.map(it => (
                <div key={it.id} className={`lux-card p-4 border-l-4 ${st.border}`} data-testid={`emp-extra-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#d4af37] uppercase tracking-widest">
                        {EXTRA_LABELS[it.extra_id] || it.extra_id}
                      </div>
                      {it.note && <div className="text-sm text-[#a0b2af] mt-1 italic">"{it.note}"</div>}
                      {it.admin_note && (
                        <div className="text-xs text-[#fdfbf7] mt-2 p-2 bg-[#0a3a33] border-l-2 border-[#d4af37]">
                          <span className="text-[#d4af37] uppercase tracking-widest text-[9px]">Nota do admin: </span>
                          {it.admin_note}
                        </div>
                      )}
                      <div className="text-[10px] text-[#a0b2af] mt-2">
                        {format(parseISO(it.created_at), "d 'de' MMM 'às' HH:mm", { locale: pt })}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${st.border} ${st.color} flex-shrink-0`}>
                      {st.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
